import { useMemo, useState } from 'react';
import { useClients, useLatestClientPhotoUrls, useTodaysVisits, type ClientRow } from '@busy/api';
import { fmtMMSS } from '@busy/core';
import { Avatar, ModalCloseButton, ModalShell } from '@busy/ui';
import { getTotalElapsedSec, useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { EodWrapupModal } from './EodWrapupModal';
import { NoShowPickerModal } from './NoShowPickerModal';

// Today's Chair — the entry view for the Cut Timer.
// Shows the day at a glance: active-cut resume banner, today's completed cuts, totals,
// and a sticky "Start a cut" CTA at the bottom.
export function TodaysChair() {
  const startNewCut = useCutTimer((s) => s.startNewCut);
  const resumeRunningCut = useCutTimer((s) => s.resumeRunningCut);
  const resetAll = useCutTimer((s) => s.resetAll);
  const closeModal = useCutTimer((s) => s.closeModal);
  const cutType = useCutTimer((s) => s.cutType);
  const sessionStartIso = useCutTimer((s) => s.startedAtIso);
  const client = useCutTimer((s) => s.client);
  const tickCount = useCutTimer((s) => s.tickCount);
  // Subscribe to tickCount for the live elapsed display in the resume banner
  void tickCount;
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [showWrapup, setShowWrapup] = useState(false);
  const [showNoShow, setShowNoShow] = useState(false);

  const visits = useTodaysVisits();
  const clients = useClients();
  const photoUrls = useLatestClientPhotoUrls();
  const t = useT();
  const language = useUI((s) => s.language);

  // Map client_id → client for quick lookup
  const clientById = useMemo(() => {
    const m = new Map<string, ClientRow>();
    (clients.data ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [clients.data]);

  const totals = useMemo(() => {
    const list = visits.data ?? [];
    return {
      count: list.length,
      revenue: list.reduce((sum, v) => sum + (v.price_cents ?? 0), 0),
    };
  }, [visits.data]);

  const cutInProgress = !!(cutType && sessionStartIso);
  const liveElapsed = cutInProgress
    ? Math.floor(getTotalElapsedSec(useCutTimer.getState()))
    : 0;

  function handleWalkIn() {
    if (cutInProgress) {
      setShowSwitchConfirm(true);
    } else {
      startNewCut();
    }
  }

  function handleDiscardAndSwitch() {
    resetAll();
    setShowSwitchConfirm(false);
    // resetAll sets mode to 'idle'; advance to checkin for the new cut.
    setTimeout(() => startNewCut(), 50);
  }

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mb-2">
          <ScissorsIcon className="w-3 h-3" />
          {t('cut.chair.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.chair.title')}</h2>
        <p className="text-white/70 text-sm mt-1.5">
          {visits.loading
            ? t('cut.chair.loading')
            : totals.count === 0
            ? t('cut.chair.empty')
            : t(totals.count === 1 ? 'cut.chair.summary_one' : 'cut.chair.summary_other', {
                cuts: totals.count,
                rev: (totals.revenue / 100).toFixed(0),
              })}
        </p>
      </div>

      <div className="p-5 space-y-3 max-h-[60dvh] overflow-y-auto">
        {/* Active cut resume banner */}
        {cutInProgress && (
          <ResumeBanner
            cutTypeName={cutType?.nameKey ? t(cutType.nameKey) : cutType?.name ?? ''}
            elapsed={liveElapsed}
            onResume={resumeRunningCut}
          />
        )}

        {/* EOD wrap-up CTA — only shows when there's at least one logged visit today
            and no cut is currently active (don't surface mid-shift). */}
        {!cutInProgress && totals.count > 0 && (
          <button
            type="button"
            onClick={() => setShowWrapup(true)}
            className="w-full text-left rounded-xl border border-navy-900/15 bg-gradient-to-br from-navy-900 to-navy-800 text-white p-4 hover:from-navy-800 hover:to-navy-700 transition-colors shadow-md"
          >
            <div className="text-[10px] tracking-[0.18em] uppercase font-extrabold text-gold-400 inline-flex items-center gap-1.5 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
              {t('cut.chair.wrapEyebrow')}
            </div>
            <div className="font-display text-lg leading-tight">
              {t(totals.count === 1 ? 'cut.chair.summary_one' : 'cut.chair.summary_other', {
                cuts: totals.count,
                rev: (totals.revenue / 100).toFixed(0),
              })}
            </div>
            <div className="text-[12px] text-white/70 mt-0.5">{t('cut.chair.wrapBody')}</div>
          </button>
        )}

        {/* Visit cards */}
        {visits.loading && (
          <ul className="space-y-2">
            {[0, 1].map((i) => (
              <li key={i} className="h-16 rounded-lg bg-bg-alt animate-pulse" />
            ))}
          </ul>
        )}

        {visits.data && visits.data.length === 0 && !cutInProgress && (
          <div className="rounded-xl border border-dashed border-line p-8 text-center">
            <ScissorsIcon className="w-7 h-7 mx-auto text-ink-500 mb-2" />
            <p className="text-sm text-ink-700 font-semibold">{t('cut.chair.noCutsYet')}</p>
            <p className="text-xs text-ink-500 mt-1">{t('cut.chair.noCutsHint')}</p>
          </div>
        )}

        {visits.data && visits.data.length > 0 && (
          <section>
            <div className="text-[11px] tracking-[0.12em] uppercase font-bold text-ink-500 mb-2">
              {t('cut.chair.completedEarlier')}
            </div>
            <ul className="space-y-2">
              {visits.data.map((v) => {
                const client = v.client_id ? clientById.get(v.client_id) : null;
                const name = client
                  ? `${client.first_name} ${client.last_name}`.trim() || t('today.unnamed')
                  : t('today.walkin');
                const time = new Date(v.started_at).toLocaleTimeString(
                  language === 'es' ? 'es-US' : 'en-US',
                  { hour: 'numeric', minute: '2-digit' },
                );
                const duration = v.duration_min ?? 0;
                const price = ((v.price_cents ?? 0) / 100).toFixed(0);
                return (
                  <li
                    key={v.id}
                    className="rounded-xl border border-line bg-surface p-3 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-50 dark:bg-green-600/20 grid place-items-center text-green-600 dark:text-green-300 shrink-0">
                      <CheckIcon className="w-5 h-5" />
                    </div>
                    <div className="text-sm font-semibold tabular-nums w-14 shrink-0">{time}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {client && (
                          <Avatar
                            name={name}
                            size="sm"
                            photoUrl={v.client_id ? photoUrls.data?.[v.client_id] : undefined}
                          />
                        )}
                        <strong className="truncate">{name}</strong>
                        {!client && (
                          <span className="text-[10px] uppercase tracking-wide font-bold bg-bg-alt text-ink-500 px-1.5 py-0.5 rounded">
                            {t('cut.chair.walkin')}
                          </span>
                        )}
                      </div>
                      {v.cut_type && (
                        <div className="text-xs text-ink-500 truncate">{v.cut_type}</div>
                      )}
                    </div>
                    <div className="text-right text-sm shrink-0">
                      <div className="font-bold tabular-nums">${price}</div>
                      <div className="text-[11px] text-ink-500 tabular-nums">{duration} min</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {visits.error && (
          <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
            {t('cut.chair.errorVisits')} {visits.error}
          </div>
        )}
      </div>

      {/* Sticky bottom action — primary "Start a cut" CTA + secondary
          "Mark no-show" tracker. The no-show button sits below the
          primary so it never crowds the main action; styling is
          subdued (text-only, amber tint) to signal "this is for
          something that didn't happen" rather than a positive action.
          Tapping opens NoShowPickerModal which logs to no_shows + the
          barber can then start a cut to fill the slot if they want. */}
      <div className="border-t border-line bg-bg-alt p-4 space-y-2">
        <button
          type="button"
          onClick={handleWalkIn}
          className="w-full h-14 rounded-xl bg-gold-500 text-navy-900 font-display text-xl shadow-lg shadow-gold-500/30 hover:bg-gold-400 transition-colors inline-flex items-center justify-center gap-2"
        >
          <UserPlusIcon className="w-5 h-5" />
          {t('cut.chair.cta')}
        </button>
        <button
          type="button"
          onClick={() => setShowNoShow(true)}
          className="w-full h-10 rounded-lg bg-transparent text-amber-700 dark:text-amber-400 font-bold text-sm hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors inline-flex items-center justify-center gap-1.5"
        >
          <NoShowIcon className="w-4 h-4" />
          {t('cut.chair.noShowCta')}
        </button>
      </div>

      {showSwitchConfirm && (
        <SwitchClientConfirmModal
          currentClientName={
            client?.kind === 'existing'
              ? `${client.firstName} ${client.lastName}`.trim() || t('cut.switch.fallbackName')
              : t('cut.switch.fallbackWalkin')
          }
          currentCutName={
            cutType?.nameKey ? t(cutType.nameKey) : cutType?.name ?? ''
          }
          elapsedMin={Math.max(1, Math.round(liveElapsed / 60))}
          onDiscard={handleDiscardAndSwitch}
          onCancel={() => setShowSwitchConfirm(false)}
        />
      )}

      {showWrapup && <EodWrapupModal onClose={() => setShowWrapup(false)} />}
      {showNoShow && <NoShowPickerModal onClose={() => setShowNoShow(false)} />}
    </>
  );
}

function SwitchClientConfirmModal({
  currentClientName,
  currentCutName,
  elapsedMin,
  onDiscard,
  onCancel,
}: {
  currentClientName: string;
  currentCutName: string;
  elapsedMin: number;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <ModalShell open onClose={onCancel} size="sm" ariaLabel={t('cut.switch.aria')}>
      <div className="p-5 space-y-3">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-red-600">
          {t('cut.switch.eyebrow')}
        </div>
        <h3 className="font-display text-xl leading-tight">
          {t('cut.switch.title', { name: currentClientName })}
        </h3>
        <p className="text-sm text-ink-700 leading-relaxed">
          {t('cut.switch.body', { min: elapsedMin, cut: currentCutName })}
        </p>
        <div className="flex flex-col gap-2 pt-2">
          <button
            type="button"
            onClick={onDiscard}
            className="w-full h-11 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700"
          >
            {t('cut.switch.discard')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full h-11 rounded-lg border border-line bg-surface font-semibold"
          >
            {t('cut.switch.cancel')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ResumeBanner({
  cutTypeName,
  elapsed,
  onResume,
}: {
  cutTypeName: string;
  elapsed: number;
  onResume: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onResume}
      className="w-full text-left rounded-xl bg-gradient-to-br from-green-600 to-green-700 text-white p-4 shadow-lg shadow-green-700/30 hover:from-green-500 hover:to-green-600 transition-colors"
    >
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold inline-flex items-center gap-1.5 mb-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-200 animate-pulse" />
        {t('cut.resume.eyebrow')}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl leading-tight truncate">{cutTypeName}</div>
          <div className="text-sm opacity-90 mt-0.5 tabular-nums">
            {fmtMMSS(elapsed)} {t('cut.resume.runningSuffix')}
          </div>
        </div>
        <div className="inline-flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full text-sm font-bold shrink-0">
          <PlayIcon className="w-3.5 h-3.5" />
          {t('cut.resume.button')}
        </div>
      </div>
    </button>
  );
}

function ScissorsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6 4 20 12 6 20" />
    </svg>
  );
}

/** Calendar with line-through — secondary action icon for the
 *  no-show button. Reads as "scheduled but didn't happen." */
function NoShowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="9" y1="14" x2="15" y2="20" />
      <line x1="15" y1="14" x2="9" y2="20" />
    </svg>
  );
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
